import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";

import DocumentItem from "@covertree/ui/content/DocumentItem";
import { InformationBox } from "@covertree/ui/content";
import { Button, LoadingFrame } from "@covertree/ui";

import { RootState } from "@redux/store";
import { useAppSelector } from "@redux/hooks";

import {
  selectClaimsDocumentsSummary,
  selectClaimReporterSummary,
  selectLossLocationDetailsSummary,
  selectPolicyDetailsSummary,
} from "@redux/slices";

import { useCreateClaim } from "./useCreateClaim";
import { SummaryBox } from "./SummaryBox";
import { SummaryItem } from "./SummaryItem";

const buildSummaryBoxes = (root: RootState) => {
  const policyDetails = selectPolicyDetailsSummary(root);
  const lossLocationDetails = selectLossLocationDetailsSummary(root);
  const claimsDocuments = selectClaimsDocumentsSummary(root);
  const claimReporter = selectClaimReporterSummary(root);

  return [policyDetails, lossLocationDetails, claimsDocuments, claimReporter];
};

export function ClaimSummary() {
  const navigate = useNavigate();

  const boxes = useAppSelector(buildSummaryBoxes);
  const { createClaim, loading } = useCreateClaim();

  if (loading) {
    return (
      <LoadingFrame
        image="/animations/spinner.gif"
        maxLoadTime={5}
        messages={["Creating claim", "Uploading files"]}
      />
    );
  }

  return (
    <>
      <Box mt={4} mb={6}>
        {boxes.map((box) => {
          return box.items.length > 0 ? (
            <SummaryBox
              navigate={() => navigate(box.navigate)}
              title={box.boxTitle}
              key={box.boxTitle}>
              {box.items.map(({ label, value, fullWidth }) => {
                if (value instanceof File) {
                  return (
                    <Grid item xs={12} key={label}>
                      <DocumentItem name={label} />
                    </Grid>
                  );
                }

                return (
                  <Grid item xs={12} sm={fullWidth ? 12 : 6} key={label}>
                    <SummaryItem label={label} value={value as string} />
                  </Grid>
                );
              })}
            </SummaryBox>
          ) : null;
        })}
      </Box>
      <InformationBox data-testid="legal-information-box" mb={4}>
        <Typography color="textPrimary">
          By submitting your claim Information, you are agreeing that you have
          answered the previous questions honestly, to the best of your ability,
          and understand your state’s&nbsp;
          <Link
            color="secondary"
            href="https://www.covertree.com/legal/fraud-statement/"
            target="_blank">
            Fraud Statement
          </Link>
          .
        </Typography>
      </InformationBox>
      <Button
        onClick={createClaim}
        sx={(theme) => ({ marginBottom: theme.spacing(5) })}>
        Submit Claim Information
      </Button>
    </>
  );
}
